<template>
	<div class="communicate-details">
		<h3 class="diagnosis-title">通讯详情</h3>
		<div class="search-modal">
			<gc-custom-search
				class="search"
				key-word="通讯服务节点"
				type="select"
				width="300px"
				:search-option="nodeTypeOptions"
				:search-option-key="{ label: 'name', value: 'defaultValue' }"
				:search.sync="searchParams.nodeType"
				needAllForSearch
			></gc-custom-search>
			<gc-custom-search
				class="search"
				key-word="通讯时间"
				type="date"
				datePickerType="datetimerange"
				width="550px"
				value-format="timestamp"
				required
				:search.sync="searchParams.time"
				:picker-options="pickerOptions"
			></gc-custom-search>
			<el-button type="primary" @click="handleQuery">查 询</el-button>
		</div>
		<div class="content" v-loading="loading">
			<gc-table border :columns="columns" :table-data="tableData" :row-class-name="setRowClassName"></gc-table>
			<div class="log-pagination">
				<span>{{ totalText }}，第 {{ pagination.pageNo }} 页</span>
				<div v-if="!allNodes">
					<el-button size="mini" :disabled="pagination.pageNo <= 1 || loading" @click="handlePageChange(-1)">
						上一页
					</el-button>
					<el-button size="mini" :disabled="!hasNext || loading" @click="handlePageChange(1)">
						下一页
					</el-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { apiTraceCommunicateLog } from '@/apis/diagnosis.api'
import { nameTrans, copyToClipboard } from '@/utils'
import { isBlank } from '@/utils/validate'

export default {
	props: {
		detail: {
			type: Object,
			default: () => {
				return {}
			},
		},
		diagnosisMode: {
			type: String,
			default: 'DEFAULT',
		},
	},
	data() {
		return {
			searchParams: {
				nodeType: null,
				time: [],
			},
			pickerOptions: {
				disabledDate(time) {
					const today = new Date()
					const daysAgo = new Date(today)
					daysAgo.setDate(today.getDate() - 30)
					return time.getTime() < daysAgo.getTime()
				},
			},
			columns: [
				{
					key: 'timestamp',
					name: '时间',
				},
				{
					key: 'nodeType',
					name: '节点',
					render: (h, row) => {
						return h(
							'span',
							{},
							isBlank(row.nodeType) ? '--' : nameTrans(row.nodeType, this.nodeTypeOptions),
						)
					},
				},
				{
					key: 'rawData',
					name: '原始数据',
					render: (h, row) => {
						return h(
							'div',
							{ class: 'can-copy-row' },
							[
								h('p', { attrs: { title: row.rawData || undefined } }, row.rawData || '--'),
								row.rawData
									? h(
											'span',
											{
												on: {
													click: () => {
														this.handleCopyToClipboard(row.rawData)
													},
												},
											},
											'复制',
									  )
									: null,
							].filter(Boolean),
						)
					},
				},
				{
					key: 'data',
					name: '数据解析',
					render: (h, row) => {
						return h(
							'div',
							{ class: 'can-copy-row' },
							[
								h('p', { attrs: { title: row.data || undefined } }, row.data || '--'),
								row.data
									? h(
											'span',
											{
												on: {
													click: () => {
														this.handleCopyToClipboard(row.data)
													},
												},
											},
											'复制',
									  )
									: null,
							].filter(Boolean),
						)
					},
				},
				{
					key: 'deviceAlarmList',
					name: '设备告警项信息',
				},
				{
					key: 'logKeywords',
					name: '日志关键词',
				},
			],
			tableData: [],
			pagination: {
				pageNo: 1,
				pageSize: 20,
				total: 0,
			},
			hasTotalCount: false,
			hasNext: false,
			loading: false,
		}
	},
	computed: {
		// 通讯节点选项
		nodeTypeOptions() {
			const options = this.$store.getters.dataList.nodeType || []
			if (this.diagnosisMode === 'DEFAULT') return options
			const nodeTypes = this.diagnosisMode === 'NB_CONVERT' ? [1, 7] : [1, 2]
			const names = { 1: '运营商', 2: '连接平台', 7: 'NB转换服务' }
			return nodeTypes.map(
				value =>
					options.find(item => item.defaultValue == value) || { defaultValue: value, name: names[value] },
			)
		},
		allNodes() {
			return this.searchParams.nodeType === null || this.searchParams.nodeType === ''
		},
		totalText() {
			return this.hasTotalCount ? `共 ${this.pagination.total} 条` : `本页 ${this.tableData.length} 条`
		},
	},
	watch: {
		detail: {
			immediate: true,
			handler(val) {
				this.resetAndLoad(val)
			},
		},
		diagnosisMode() {
			this.resetAndLoad(this.detail)
		},
	},
	methods: {
		handleQuery() {
			this.pagination.pageNo = 1
			this.getCommunicateLog()
		},
		resetAndLoad(detail) {
			if (!detail || !detail.deviceNo) return
			const target = this.nodeTypeOptions.find(item => item.defaultValue == this.$route.query.node)
			this.searchParams.nodeType = target ? target.defaultValue : null
			const center = new Date(detail.lastCommunicateTime || Date.now()).getTime()
			const timestamp = Number.isNaN(center) ? Date.now() : center
			this.searchParams.time = [timestamp - 60 * 60 * 1000, timestamp + 60 * 60 * 1000]
			this.tableData = []
			this.pagination.pageNo = 1
			this.hasNext = false
			this.getCommunicateLog()
		},
		getCommunicateLog() {
			const { nodeType, time } = this.searchParams
			if (!Array.isArray(time) || time.length !== 2) {
				return this.$message.warning('通讯时间不能为空')
			}
			this.loading = true
			const {
				bizSysType,
				communicationMode,
				deviceNo,
				deviceType,
				deviceTypeId,
				imei,
				isp,
				linkPlatformDeviceId,
			} = this.detail
			const pageNo = this.allNodes ? 1 : this.pagination.pageNo
			const pageSize = this.allNodes ? 100 : 20
			const params = {
				diagnosisMode: this.diagnosisMode,
				bizSysType,
				communicationMode,
				deviceNo,
				linkPlatformDeviceId,
				deviceType,
				deviceTypeId,
				nodeType,
				imei,
				isp,
				startTime: time[0],
				endTime: time[1],
				pageNo,
				pageSize,
			}
			apiTraceCommunicateLog(params)
				.then(res => {
					this.tableData = res.records || []
					this.hasTotalCount = res.totalCount !== undefined && res.totalCount !== null
					this.pagination.total = this.hasTotalCount ? Number(res.totalCount) : 0
					this.hasNext = !this.allNodes && res.hasNext === true
				})
				.catch(() => {
					this.tableData = []
					this.hasTotalCount = false
					this.hasNext = false
				})
				.finally(() => {
					this.loading = false
				})
		},
		handlePageChange(offset) {
			this.pagination.pageNo += offset
			this.getCommunicateLog()
		},

		async handleCopyToClipboard(text) {
			const data = await copyToClipboard(text)
			if (data.success) {
				this.$message.success('复制成功')
			}
		},

		setRowClassName({ row }) {
			if (row.errorTag === 1) {
				return 'error-row'
			}
			return ''
		},
	},
}
</script>
<style lang="scss" scoped>
.communicate-details {
	display: flex;
	flex-direction: column;
	height: 100%;
	.search-modal {
		padding: 16px 0;
		display: flex;
		align-items: center;
		.search {
			margin-right: 12px;
		}
	}

	.content {
		flex: 1;
		height: 0;

		::v-deep .error-row {
			background-color: #e5662e1a;
		}
		.log-pagination {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 12px 0;
			color: #5f667d;
		}
	}

	::v-deep .can-copy-row {
		display: flex;
		p {
			flex: 1;
			width: 0;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
		span {
			flex-shrink: 0;
			cursor: pointer;
			color: #2b57e8;
			margin-left: 8px;
		}
	}
}
</style>
